/**
 * Centralized sitemap URL registry.
 * BOTH the HTML sitemap page (Sitemap.tsx) and the XML sitemap generator
 * (scripts/generate-sitemap.ts via Vite plugin) read from this file.
 *
 * ⚠️  When you add a new page, add it here so BOTH sitemaps stay in sync.
 */

import { allElectricalServices, hvacServices, energyServices, industryServices, stadiumServices } from "@/data/services";
import { allLocations } from "@/data/locations";
import { blogPosts } from "@/data/blog-posts";
import { guides } from "@/data/guides";
import { resolveRedirect } from "@/lib/redirect-map";

export interface SitemapEntry {
  path: string;
  label: string;
  changefreq: "daily" | "weekly" | "monthly" | "yearly";
  priority: number;
  /** ISO 8601 date (YYYY-MM-DD). When omitted, generator falls back to today. */
  lastmod?: string | undefined;
}

// ── Main Pages ──────────────────────────────────────────
export const mainPages: SitemapEntry[] = [
  { path: "/", label: "Home", changefreq: "weekly", priority: 1.0 },
  { path: "/about", label: "About", changefreq: "monthly", priority: 0.7 },
  { path: "/about/safety", label: "Safety", changefreq: "monthly", priority: 0.5 },
  { path: "/about/careers", label: "Careers", changefreq: "monthly", priority: 0.5 },
  { path: "/about/faq", label: "FAQ", changefreq: "monthly", priority: 0.6 },
  { path: "/portfolio", label: "Portfolio", changefreq: "monthly", priority: 0.7 },
  { path: "/reviews", label: "Reviews", changefreq: "monthly", priority: 0.7 },
  { path: "/contact", label: "Contact", changefreq: "monthly", priority: 0.8 },
  { path: "/request-estimate", label: "Request Estimate", changefreq: "monthly", priority: 0.8 },
  { path: "/privacy-policy", label: "Privacy Policy", changefreq: "yearly", priority: 0.3 },
  { path: "/sitemap", label: "Sitemap", changefreq: "monthly", priority: 0.3 },
  { path: "/blog", label: "Blog", changefreq: "weekly", priority: 0.7 },
];

// ── Blog Posts ───────────────────────────────────────────
// Use each post's modifiedDate (or publishedDate) as <lastmod> so Bing/Google
// can detect real updates instead of seeing every URL change every build.
export const blogPages: SitemapEntry[] = blogPosts.map(p => ({
  path: `/blog/${p.slug}`,
  label: p.title,
  changefreq: "monthly" as const,
  priority: 0.7,
  lastmod: (p.modifiedDate || p.publishedDate || "").slice(0, 10) || undefined,
}));

// ── Electrical Services ─────────────────────────────────
export const electricalPages: SitemapEntry[] = [
  { path: "/electrical-services", label: "Electrical Services Overview", changefreq: "monthly", priority: 0.9 },
  ...allElectricalServices.map(s => ({
    path: `/electrical-services/${s.slug}`,
    label: s.title,
    changefreq: "monthly" as const,
    priority: 0.8,
  })),
];

// ── HVAC Services ───────────────────────────────────────
export const hvacPages: SitemapEntry[] = [
  { path: "/hvac-services", label: "HVAC Services Overview", changefreq: "monthly", priority: 0.9 },
  ...hvacServices.map(s => ({
    path: `/hvac-services/${s.slug}`,
    label: s.title,
    changefreq: "monthly" as const,
    priority: 0.8,
  })),
];

// ── Energy Management ───────────────────────────────────
export const energyPages: SitemapEntry[] = [
  { path: "/energy-management", label: "Energy Management Overview", changefreq: "monthly", priority: 0.9 },
  ...energyServices.map(s => ({
    path: `/energy-management/${s.slug}`,
    label: s.title,
    changefreq: "monthly" as const,
    priority: 0.8,
  })),
];

// ── Industries ──────────────────────────────────────────
export const industryPages: SitemapEntry[] = [
  { path: "/industries", label: "Industries Overview", changefreq: "monthly", priority: 0.8 },
  ...industryServices.map(s => ({
    path: `/industries/${s.slug}`,
    label: s.title,
    changefreq: "monthly" as const,
    priority: 0.7,
  })),
];

// ── Stadium & Sports Lighting ───────────────────────────
export const stadiumPages: SitemapEntry[] = [
  { path: "/stadium-sports-lighting", label: "Stadium & Sports Lighting Overview", changefreq: "monthly", priority: 0.8 },
  ...stadiumServices.map(s => ({
    path: `/stadium-sports-lighting/${s.slug}`,
    label: s.title,
    changefreq: "monthly" as const,
    priority: 0.7,
  })),
];

// ── Authority Guides ────────────────────────────────────
// Derived from the guides data so every live guide is listed automatically.
// Guides consolidated via a 301 (see redirect-map) are excluded.
const GUIDE_OVERRIDES: Record<string, { label: string; priority: number }> = {
  "energy-management-louisville-2026": { label: "Energy Management Guide", priority: 0.7 },
  "lighting-retrofit-guide": { label: "Lighting Retrofit Guide", priority: 0.7 },
  "commercial-hvac-guide-louisville": { label: "Commercial HVAC Guide", priority: 0.7 },
  "stadium-sports-lighting-guide": { label: "Stadium Lighting Guide", priority: 0.7 },
  "industrial-electrical-safety-guide": { label: "Electrical Safety Guide", priority: 0.7 },
  "residential-hvac-guide-louisville": { label: "Residential HVAC Guide", priority: 0.7 },
  "electricians-louisville-kentucky": { label: "Electricians Louisville Kentucky", priority: 0.8 },
  "electric-companies-louisville-ky": { label: "Electric Companies Louisville KY", priority: 0.8 },
  "residential-electrician-louisville-ky": { label: "Residential Electrician Louisville KY", priority: 0.8 },
  "hvac-maintenance-service-guide": { label: "HVAC Maintenance Service Guide", priority: 0.8 },
  "residential-electrical-services-guide": { label: "Residential Electrical Services Guide", priority: 0.8 },
  "hvac-installation-louisville-ky": { label: "HVAC Installation Louisville KY", priority: 0.9 },
  "air-conditioning-repair-louisville-ky": { label: "Air Conditioning Repair Louisville KY", priority: 0.9 },
  "heating-repair-louisville-ky": { label: "Heating Repair Louisville KY", priority: 0.9 },
  "ac-tune-up-louisville-ky": { label: "AC Tune-Up Louisville KY", priority: 0.9 },
  "air-conditioning-installation-louisville-ky": { label: "Air Conditioning Installation Louisville KY", priority: 0.9 },
  "solar-panel-installation-louisville-ky": { label: "Solar Panel Installation Louisville KY", priority: 0.9 },
  "commercial-electrical-costs-louisville-ky": { label: "Commercial Electrical Costs Louisville KY", priority: 0.9 },
};
export const guidePages: SitemapEntry[] = guides
  .filter(g => !resolveRedirect(`/guide/${g.slug}`))
  .map(g => ({
    path: `/guide/${g.slug}`,
    label: GUIDE_OVERRIDES[g.slug]?.label ?? g.title,
    changefreq: "monthly" as const,
    priority: GUIDE_OVERRIDES[g.slug]?.priority ?? 0.8,
  }));

// ── Service Areas ───────────────────────────────────────
export const serviceAreaPages: SitemapEntry[] = [
  { path: "/service-area", label: "Service Area Overview", changefreq: "monthly", priority: 0.8 },
  ...allLocations.map(l => ({
    path: `/service-area/${l.slug}`,
    label: `${l.city}, ${l.state}`,
    changefreq: "monthly" as const,
    priority: 0.6,
  })),
];

// ── All URLs (for XML sitemap generation) ───────────────
export const allSitemapUrls: SitemapEntry[] = [
  ...mainPages,
  ...blogPages,
  ...electricalPages,
  ...hvacPages,
  ...energyPages,
  ...industryPages,
  ...stadiumPages,
  ...guidePages,
  ...serviceAreaPages,
];
